export enum ErrorCode {
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  NOT_FOUND = 'NOT_FOUND',
  FORBIDDEN = 'FORBIDDEN',
  UNAUTHORIZED = 'UNAUTHORIZED',
  INVALID_TRANSITION = 'INVALID_TRANSITION',
  GATE_FAILED = 'GATE_FAILED',
  REASON_REQUIRED = 'REASON_REQUIRED',
  DOMAIN_CONFLICT = 'DOMAIN_CONFLICT',
  LOGISTICS_CALCULATION_NOT_PUBLISH_READY = 'LOGISTICS_CALCULATION_NOT_PUBLISH_READY',
  LOGISTICS_CALCULATION_CARGO_HELD = 'LOGISTICS_CALCULATION_CARGO_HELD',
  INTERNAL_ERROR = 'INTERNAL_ERROR',
  AUTH_INVALID_CREDENTIALS = 'AUTH_INVALID_CREDENTIALS',
  AUTH_TOKEN_INVALID = 'AUTH_TOKEN_INVALID',
  RATE_LIMIT_EXCEEDED = 'RATE_LIMIT_EXCEEDED',
  AUTH_REFRESH_INVALID = 'AUTH_REFRESH_INVALID',
  AUTH_REFRESH_REUSED = 'AUTH_REFRESH_REUSED',
  USER_NOT_FOUND = 'USER_NOT_FOUND',
  USER_EMAIL_TAKEN = 'USER_EMAIL_TAKEN',
  CANNOT_MODIFY_SELF = 'CANNOT_MODIFY_SELF',
  DEPARTMENT_NOT_FOUND = 'DEPARTMENT_NOT_FOUND',
  DEPARTMENT_NAME_TAKEN = 'DEPARTMENT_NAME_TAKEN',
  DEPARTMENT_ARCHIVED = 'DEPARTMENT_ARCHIVED',
  UNKNOWN_ROLE = 'UNKNOWN_ROLE',
  SYSTEM_ROLE_IMMUTABLE = 'SYSTEM_ROLE_IMMUTABLE',
  ARCHIVED_ROLE_ASSIGNMENT = 'ARCHIVED_ROLE_ASSIGNMENT',
  MISSING_CO_GRANT = 'MISSING_CO_GRANT',
  ROLE_PERMISSIONS_STALE = 'ROLE_PERMISSIONS_STALE',
  INVALID_PHONE = 'INVALID_PHONE',
  NO_LOGIST_AVAILABLE = 'NO_LOGIST_AVAILABLE',
  SHIFT_CANNOT_LEAVE_LAST = 'SHIFT_CANNOT_LEAVE_LAST',
  ACTOR_HAS_OPEN_ASSIGNMENT = 'ACTOR_HAS_OPEN_ASSIGNMENT',
  STORAGE_FORBIDDEN = 'STORAGE_FORBIDDEN',
  REVENUE_NOT_CONFIGURED = 'REVENUE_NOT_CONFIGURED',
  REVENUE_SOURCE_UNAVAILABLE = 'REVENUE_SOURCE_UNAVAILABLE',
  REVENUE_SOURCE_INVALID = 'REVENUE_SOURCE_INVALID',
  REVENUE_INVALID_MONTH = 'REVENUE_INVALID_MONTH',
  REVENUE_MONTH_NOT_FOUND = 'REVENUE_MONTH_NOT_FOUND',
  // WHY (#1605, epic #1604): write attempted on the Cargo registry while the
  //   service is in read-only mirror mode (`CARGO_READONLY_MIRROR=1`). Signals
  //   the continuous-sync bridge is active; mutations are deferred to NextKargo.
  //   HTTP 423 Locked (or 503 Service Unavailable at the endpoint layer).
  CARGO_READONLY_MIRROR = 'CARGO_READONLY_MIRROR',
  // WHY (#601): mutation attempted on a contact / legal-entity nested under
  //   an archived client. Carries `meta.clientId` so the UI can render a
  //   "restore parent first" CTA. HTTP 409 Conflict.
  CLIENT_ARCHIVED_READONLY = 'CLIENT_ARCHIVED_READONLY',
  // WHY (#1256): call-center agent has no `CallCenterAgentProfile` source
  //   binding; the submit endpoint (#1258) rejects. HTTP 422.
  CALL_CENTER_NO_PROFILE = 'CALL_CENTER_NO_PROFILE',
  // WHY (#1256): the agent's bound `LeadSource` is disabled/archived;
  //   the submit endpoint (#1258) rejects. HTTP 422.
  CALL_CENTER_SOURCE_DISABLED = 'CALL_CENTER_SOURCE_DISABLED',
  // WHY (#1397): the agent re-submitted a phone they have ALREADY
  //   successfully submitted (own accepted history). Distinct from
  //   `PHONE_ALREADY_EXISTS` (manager-facing): this is the deliberate
  //   own-history exception to the blind contract and leaks nothing about
  //   OTHER agents or global leads. The submit endpoint rejects. HTTP 409.
  CALL_CENTER_DUPLICATE_OWN_PHONE = 'CALL_CENTER_DUPLICATE_OWN_PHONE',
  // WHY (#1359): a write would collide with the single global
  //   `ContactPhoneKey.phoneKey` slot — the normalized phone already
  //   belongs to another Lead/ClientContact. Surfaced through the
  //   standard `ApiError` envelope; no emitter is wired in #1359. HTTP 409.
  PHONE_ALREADY_EXISTS = 'PHONE_ALREADY_EXISTS',
  // WHY (#1869): the AI analyst agent is not configured (model env vars or
  //   sandbox not present); the feature is inert. HTTP 503 Service Unavailable.
  AGENT_NOT_CONFIGURED = 'AGENT_NOT_CONFIGURED',
  // WHY (#1869): the sandbox AST/role checker rejected the SQL generated by
  //   the agent — the query touched disallowed tables or used forbidden
  //   constructs. HTTP 422 Unprocessable Entity.
  AGENT_SQL_REJECTED = 'AGENT_SQL_REJECTED',
  // WHY (#1869): the sandbox statement exceeded its execution time limit.
  //   The orchestrator surfaces this as a terminal run failure. HTTP 504.
  AGENT_SQL_TIMEOUT = 'AGENT_SQL_TIMEOUT',
  // WHY (#1869): the orchestrator run failed terminally after exhausting
  //   retries or hitting an unrecoverable error. Distinct from SQL-specific
  //   codes so the client can render a generic "try again" CTA. HTTP 500.
  AGENT_RUN_FAILED = 'AGENT_RUN_FAILED',
  // WHY (#1936): the client-payment review flow was acted on while it is not
  //   open — either a Manager submitted a desired-status-only change on a row
  //   already committed to paid/partially_paid (no file change reopened
  //   review), or a financier review batch hit a row that is not
  //   `under_review`. HTTP 409 Conflict.
  CARGO_PAYMENT_REVIEW_NOT_OPEN = 'CARGO_PAYMENT_REVIEW_NOT_OPEN',
  // WHY (#1936): a financier review decision named a documentId that does not
  //   belong to the cargo row under review, or is soft-deleted. HTTP 400.
  CARGO_PAYMENT_DOCUMENT_UNKNOWN = 'CARGO_PAYMENT_DOCUMENT_UNKNOWN',
  // WHY (#1936): after applying the financier decisions an active document
  //   marked `accepted` still has no `receivedRub` value, so the whole review
  //   commit is rolled back. HTTP 422 Unprocessable Entity.
  CARGO_PAYMENT_REVIEW_INCOMPLETE = 'CARGO_PAYMENT_REVIEW_INCOMPLETE',
  // WHY (#1936): a soft-delete was attempted on a payment document whose
  //   `reviewStatus` is no longer `pending` (already accepted/rejected by the
  //   financier). Managers may delete only pending docs. HTTP 409 Conflict.
  CARGO_PAYMENT_DOCUMENT_LOCKED = 'CARGO_PAYMENT_DOCUMENT_LOCKED',
  // WHY (#1936): the Manager submitted a client-payment status on a cargo row
  //   with NO active (non-deleted) payment document. The submit gate requires
  //   at least one proof document. Distinct from the legacy gated 409 — submit
  //   wants a 400. HTTP 400 Bad Request.
  CARGO_PAYMENT_NO_ACTIVE_DOCUMENT = 'CARGO_PAYMENT_NO_ACTIVE_DOCUMENT',
  // WHY (#2866): the actor's issued cargo-problem video key has already been
  // claimed or cleaned by expiry cleanup. The client must issue and upload a
  // fresh key; `meta.expiredObjectKey` identifies only that actor-owned item.
  CARGO_PROBLEM_UPLOAD_EXPIRED = 'CARGO_PROBLEM_UPLOAD_EXPIRED',
  // WHY (#2006, epic #2004): the v2 agent's LLM call exceeded its time budget
  //   before a usable response arrived. Terminal run failure. HTTP 502.
  AGENT_MODEL_TIMEOUT = 'AGENT_MODEL_TIMEOUT',
  // WHY (#2006, epic #2004): the upstream model provider returned a transport
  //   or HTTP error (5xx / connection reset). Terminal run failure. HTTP 502.
  AGENT_MODEL_UPSTREAM_ERROR = 'AGENT_MODEL_UPSTREAM_ERROR',
  // WHY (#2006, epic #2004): the model response could not be parsed into the
  //   expected tool-call / message shape (truncated or invalid JSON). HTTP 502.
  AGENT_MODEL_MALFORMED_RESPONSE = 'AGENT_MODEL_MALFORMED_RESPONSE',
  // WHY (#2006, epic #2004): a tool invocation failed for a reason not covered
  //   by a more specific code. Surfaced on a tool_call_result. HTTP 502.
  AGENT_TOOL_FAILED = 'AGENT_TOOL_FAILED',
  // WHY (#2006, epic #2004): a SQL/analytics tool produced more rows than the
  //   result cap allows; the result is refused rather than streamed. HTTP 422.
  AGENT_SQL_RESULT_TOO_LARGE = 'AGENT_SQL_RESULT_TOO_LARGE',
  // WHY (#2006, epic #2004): the question targets a domain the agent has no
  //   tools/schema for and cannot answer. HTTP 422.
  AGENT_UNSUPPORTED_DOMAIN = 'AGENT_UNSUPPORTED_DOMAIN',
  // WHY (#2006, epic #2004): the agent needs the user to disambiguate before
  //   it can proceed; pairs with a clarification_requested event. HTTP 422.
  AGENT_NEEDS_CLARIFICATION = 'AGENT_NEEDS_CLARIFICATION',
  // WHY (#2006, epic #2004): a referenced schema/table/metric is unknown to
  //   the current catalog. HTTP 422.
  AGENT_SCHEMA_UNKNOWN = 'AGENT_SCHEMA_UNKNOWN',
  // WHY (#2006, epic #2004): the caller lacks permission for the requested
  //   data or action under the agent's read-only role. HTTP 403.
  AGENT_PERMISSION_DENIED = 'AGENT_PERMISSION_DENIED',
  // WHY (#2006, epic #2004): an unexpected internal error in the v2
  //   orchestrator distinct from model/tool/SQL codes. HTTP 500.
  AGENT_INTERNAL_ERROR = 'AGENT_INTERNAL_ERROR',
  // WHY (#2006, epic #2004): the run hit its token / step / cost budget and
  //   was stopped before completing. HTTP 422.
  AGENT_BUDGET_EXCEEDED = 'AGENT_BUDGET_EXCEEDED',
  // WHY (#2006, epic #2004): a payload failed Zod validation at an internal
  //   contract boundary (model output or tool result). HTTP 422.
  AGENT_ZOD_PARSE_ERROR = 'AGENT_ZOD_PARSE_ERROR',
  // WHY (#2006, epic #2004): the client disconnected or the run was cancelled
  //   mid-stream; the SSE stream was aborted. HTTP 499 (client closed) / 500.
  AGENT_STREAM_ABORTED = 'AGENT_STREAM_ABORTED',
  // WHY (#2265, epic #2240): a write was attempted on the legacy Deal aggregate
  //   while its write surface is frozen (`DEAL_WRITES_FROZEN=1`, the default) —
  //   under CRM v4 Option-1 coexistence the canonical deal is
  //   `Engagement(phase=DEAL)`, created by marking a лид paid, so the legacy
  //   create/update/stage/archive/restore endpoints reject. Distinct from a
  //   generic `DOMAIN_CONFLICT` so the UI can route the caller to the v4 flow.
  //   HTTP 409 Conflict. Flag-reversible (mirrors CARGO_READONLY_MIRROR).
  DEAL_WRITES_FROZEN = 'DEAL_WRITES_FROZEN',
}
